import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, X, Link, ChevronRight, Timer, Check, Search, ChevronDown, Trash2 } from 'lucide-react';
import { Modal, MicButton, Confirm } from './UI';
import { useApp } from '../contexts/AppContext';
import MarkdownEditor from './MarkdownEditor';
import { api } from '../api/client';
import { toast } from './UI';

const SCOPE_LABELS = {
  task: 'Normaler Task',
  quarter: 'Quartalsziel',
  month: 'Monatsziel',
  week: 'Wochenziel',
  key_result: 'Key Result',
};
const SHOWS_PROGRESS = new Set(['quarter', 'key_result']);

const DELEGATION_LABELS = {
  mine: 'Bei mir',
  delegate: '🤔 Delegierbar',
  delegated: '✓ Delegiert',
};

const SIZES = ['XS', 'S', 'M', 'L', 'XL'];
const SIZE_HINTS = {
  XS: '< 15min',
  S: '~30min',
  M: '~1–2h',
  L: '~halber Tag',
  XL: '~Tag+',
};

const EMPTY = {
  title: '', status: 'Offen', owner: '', next_step: '', next_step_due_date: '',
  categories: [], importance: 'Mittel', urgency: 'Diese Woche',
  estimated_duration: '', size: '', is_recurring: false, recurring_interval: '', notes: '', links: [],
  parent_id: null, scope: 'task', period_date: '', delegation: 'mine',
  goal_id: null, progress: 0,
};

function descendantIds(taskId, allTasks) {
  if (!taskId) return new Set();
  const children = allTasks.filter(t => t.parent_id === taskId).map(t => t.id);
  const result = new Set([taskId]);
  for (const cid of children) {
    for (const id of descendantIds(cid, allTasks)) result.add(id);
  }
  return result;
}

// Build form state from a task or empty defaults
function taskToForm(task, statusValues, initial) {
  if (task) {
    return {
      ...task,
      next_step_due_date: task.next_step_due_date?.split('T')[0] || '',
      period_date: task.period_date?.split('T')[0] || '',
      estimated_duration: task.estimated_duration || '',
      size: task.size || '',
      links: task.links || [],
      categories: task.categories || [],
      parent_id: task.parent_id || null,
      scope: task.scope || 'task',
      delegation: task.delegation || 'mine',
      goal_id: task.goal_id || null,
      progress: task.progress || 0,
    };
  }
  return { ...EMPTY, status: statusValues[0] || 'Offen', ...(initial || {}) };
}

// Searchable parent picker — replaces the long unfilterable <select>
function ParentPicker({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = value ? options.find(o => o.id === value) : null;
  const filtered = !search.trim()
    ? options.slice(0, 50)
    : options.filter(o => o.title.toLowerCase().includes(search.toLowerCase())).slice(0, 50);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left',
          background: 'var(--bg-2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '7px 10px',
          fontSize: 13, color: selected ? 'var(--text)' : 'var(--text-3)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected
            ? <>{selected.scope && selected.scope !== 'task' ? <span style={{ color: 'var(--accent)' }}>[{SCOPE_LABELS[selected.scope]}] </span> : null}{selected.title}</>
            : '— Kein Parent —'}
        </span>
        <ChevronDown size={14} color="var(--text-3)" />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1100, marginTop: 4,
          background: 'var(--bg-1)', border: '1px solid var(--border-2)',
          borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
          padding: 6, maxHeight: 280, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ position: 'relative', marginBottom: 6 }}>
            <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Parent suchen..." style={{ paddingLeft: 26, fontSize: 12 }} />
          </div>
          <div style={{ overflow: 'auto', flex: 1 }}>
            <button type="button"
              onClick={() => { onChange(null); setOpen(false); setSearch(''); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '6px 10px', fontSize: 12, color: 'var(--text-3)',
                background: !value ? 'var(--bg-3)' : 'transparent',
                border: 'none', cursor: 'pointer', borderRadius: 4, fontStyle: 'italic',
              }}>— Kein Parent —</button>
            {filtered.length === 0 && (
              <div style={{ padding: 10, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>Keine Treffer</div>
            )}
            {filtered.map(o => (
              <button key={o.id} type="button"
                onClick={() => { onChange(o.id); setOpen(false); setSearch(''); }}
                style={{
                  display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 6,
                  padding: '6px 10px', fontSize: 12, color: 'var(--text)',
                  background: value === o.id ? 'var(--bg-3)' : 'transparent',
                  border: 'none', cursor: 'pointer', borderRadius: 4,
                }}
                onMouseEnter={e => { if (value !== o.id) e.currentTarget.style.background = 'var(--bg-2)'; }}
                onMouseLeave={e => { if (value !== o.id) e.currentTarget.style.background = 'transparent'; }}
              >
                {o.scope && o.scope !== 'task' && (
                  <span style={{ fontSize: 10, color: 'var(--accent)' }}>[{SCOPE_LABELS[o.scope]}]</span>
                )}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TaskModal({ task, initial, onClose, onSaved, onNavigate }) {
  const { tasks, statusValues, categories, importanceValues, urgencyValues, startPomodoro } = useApp();

  const [form, setForm] = useState(() => taskToForm(task, statusValues, initial));
  const [creating, setCreating] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const [newLink, setNewLink] = useState({ url: '', label: '' });
  const [goals, setGoals] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    api.getGoals().then(g => setGoals(Array.isArray(g) ? g : [])).catch(() => {});
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const lastSavedRef = useRef(JSON.stringify(form));
  const saveTimerRef = useRef(null);

  // Auto-save for existing tasks (debounced)
  useEffect(() => {
    if (!task) return; // only for editing — new tasks go through Erstellen button
    if (!form.title.trim()) return;
    const current = JSON.stringify(form);
    if (current === lastSavedRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(async () => {
      try {
        const data = {
          ...form,
          next_step_due_date: form.next_step_due_date || null,
          period_date: form.period_date || null,
          estimated_duration: form.estimated_duration ? parseInt(form.estimated_duration) : null,
          size: form.size || null,
        };
        await api.updateTask(task.id, data);
        lastSavedRef.current = current;
        setSaveStatus('saved');
        if (onSaved) onSaved();
        setTimeout(() => setSaveStatus(s => s === 'saved' ? 'idle' : s), 1500);
      } catch (err) {
        setSaveStatus('error');
        toast(err.message || 'Speichern fehlgeschlagen', 'error');
      }
    }, 700);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [form, task, onSaved]);

  const excludedFromParent = useMemo(
    () => task ? descendantIds(task.id, tasks) : new Set(),
    [task, tasks]
  );
  const parentOptions = useMemo(
    () => tasks.filter(t => !excludedFromParent.has(t.id))
      .sort((a, b) => {
        // Goals first, then sorted by created
        const sa = a.scope && a.scope !== 'task' ? 0 : 1;
        const sb = b.scope && b.scope !== 'task' ? 0 : 1;
        if (sa !== sb) return sa - sb;
        return new Date(b.created_at) - new Date(a.created_at);
      }),
    [tasks, excludedFromParent]
  );
  const children = useMemo(
    () => task ? tasks.filter(t => t.parent_id === task.id) : [],
    [task, tasks]
  );
  const parent = useMemo(
    () => form.parent_id ? tasks.find(t => t.id === form.parent_id) : null,
    [form.parent_id, tasks]
  );

  const toggleCat = (cat) => {
    set('categories', form.categories.includes(cat)
      ? form.categories.filter(c => c !== cat)
      : [...form.categories, cat]);
  };

  const addLink = () => {
    if (!newLink.url) return;
    set('links', [...form.links, { ...newLink }]);
    setNewLink({ url: '', label: '' });
  };

  const handleDelete = async () => {
    if (!task) return;
    try {
      await api.deleteTask(task.id);
      toast('Task gelöscht', 'success');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast(err.message || 'Löschen fehlgeschlagen', 'error');
    } finally {
      setConfirmDelete(false);
    }
  };

  // Explicit create for new tasks
  const create = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const data = {
        ...form,
        next_step_due_date: form.next_step_due_date || null,
        period_date: form.period_date || null,
        estimated_duration: form.estimated_duration ? parseInt(form.estimated_duration) : null,
        size: form.size || null,
      };
      await api.createTask(data);
      toast('Task erstellt', 'success');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const openTask = (t) => {
    if (onNavigate) onNavigate(t);
    else onClose();
  };

  const SaveIndicator = () => {
    if (!task) return null;
    if (saveStatus === 'saving') return <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Speichert…</span>;
    if (saveStatus === 'saved') return <span style={{ fontSize: 11, color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={11} /> Gespeichert</span>;
    if (saveStatus === 'error') return <span style={{ fontSize: 11, color: 'var(--red)' }}>Fehler beim Speichern</span>;
    return null;
  };

  return (
    <Modal title={task ? 'Task bearbeiten' : 'Neuer Task'} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Parent breadcrumb + auto-save status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {parent && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-3)', flex: 1 }}>
              <span>Parent:</span>
              <button type="button" onClick={() => openTask(parent)} style={{
                background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 2,
              }}>
                {parent.title} <ChevronRight size={11} />
              </button>
            </div>
          )}
          {!parent && <div style={{ flex: 1 }} />}
          <SaveIndicator />
        </div>

        {/* Title */}
        <div className="form-group">
          <label>Titel *</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="Was muss erledigt werden?" autoFocus />
            <MicButton onTranscript={(text) => set('title', form.title ? `${form.title} ${text}` : text)} />
          </div>
        </div>

        {/* Next step */}
        <div className="form-group">
          <label>Nächster Schritt</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={form.next_step || ''} onChange={e => set('next_step', e.target.value)} placeholder="Was ist der nächste konkrete Schritt?" />
            <MicButton onTranscript={(text) => set('next_step', form.next_step ? `${form.next_step} ${text}` : text)} />
          </div>
        </div>

        {/* Notizen — Markdown-Editor mit Live-Rendering und Screenshot-Paste */}
        <div className="form-group">
          <label>Notizen</label>
          <MarkdownEditor
            value={form.notes || ''}
            onChange={(v) => set('notes', v)}
            minHeight={200}
            mic
            storageKey="md-mode-task-notes"
          />
        </div>

        {/* Date + size grid */}
        <div className="form-grid">
          <div className="form-group">
            <label>Fälligkeitsdatum (nächster Schritt)</label>
            <input type="date" value={form.next_step_due_date} onChange={e => set('next_step_due_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Größe (Aufwand)</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {SIZES.map(s => {
                const active = form.size === s;
                return (
                  <button key={s} type="button"
                    onClick={() => set('size', active ? '' : s)}
                    title={SIZE_HINTS[s]}
                    style={{
                      flex: 1, padding: '6px 0', fontSize: 13, fontWeight: active ? 600 : 500,
                      border: '1px solid', borderColor: active ? 'var(--accent)' : 'var(--border)',
                      background: active ? 'var(--accent-dim)' : 'transparent',
                      color: active ? 'var(--accent)' : 'var(--text-2)',
                      borderRadius: 'var(--radius)', cursor: 'pointer',
                    }}>{s}</button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Status — prominent */}
        <div className="form-group">
          <label style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>Status</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {statusValues.map(s => {
              const active = form.status === s;
              return (
                <button key={s} type="button"
                  onClick={() => set('status', s)}
                  style={{
                    padding: '6px 14px', fontSize: 13, fontWeight: active ? 600 : 500,
                    border: '1px solid', borderColor: active ? 'var(--accent)' : 'var(--border)',
                    background: active ? 'var(--accent-dim)' : 'transparent',
                    color: active ? 'var(--accent)' : 'var(--text-2)',
                    borderRadius: 'var(--radius)', cursor: 'pointer',
                  }}>{s}</button>
              );
            })}
          </div>
        </div>

        {/* Children */}
        {task && children.length > 0 && (
          <div className="form-group">
            <label>Unteraufgaben ({children.length})</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {children.map(c => (
                <button type="button" key={c.id} onClick={() => openTask(c)} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                  background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                  color: 'var(--text)', cursor: 'pointer', textAlign: 'left', fontSize: 13,
                }}>
                  <ChevronRight size={12} color="var(--text-3)" />
                  {c.scope && c.scope !== 'task' && (
                    <span style={{ fontSize: 11, color: 'var(--accent)' }}>[{SCOPE_LABELS[c.scope]}]</span>
                  )}
                  <span style={{ flex: 1 }}>{c.title}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{c.status}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <hr className="divider" />

        {/* Metadata grid (status moved out) */}
        <div className="form-grid">
          <div className="form-group">
            <label>Bei wem liegt's</label>
            <input value={form.owner || ''} onChange={e => set('owner', e.target.value)} placeholder="Name oder Team" />
          </div>
          <div className="form-group">
            <label>Wichtigkeit</label>
            <select value={form.importance} onChange={e => set('importance', e.target.value)}>
              {importanceValues.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Dringlichkeit</label>
            <select value={form.urgency} onChange={e => set('urgency', e.target.value)}>
              {urgencyValues.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Delegation</label>
            <select value={form.delegation || 'mine'} onChange={e => set('delegation', e.target.value)}>
              {Object.entries(DELEGATION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Ebene</label>
            <select value={form.scope} onChange={e => set('scope', e.target.value)}>
              {Object.entries(SCOPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {(form.scope === 'quarter' || form.scope === 'month' || form.scope === 'week') && (
            <div className="form-group">
              <label>
                {form.scope === 'quarter' && 'Quartalsstart (1. Tag)'}
                {form.scope === 'month' && 'Monatsstart (1. Tag)'}
                {form.scope === 'week' && 'Wochen-Montag'}
              </label>
              <input type="date" value={form.period_date || ''} onChange={e => set('period_date', e.target.value)} />
            </div>
          )}
          {form.scope === 'quarter' && (
            <div className="form-group">
              <label>Verlinktes Jahresziel</label>
              <select value={form.goal_id || ''} onChange={e => set('goal_id', e.target.value || null)}>
                <option value="">— Kein Jahresziel —</option>
                {goals.map(g => <option key={g.id} value={g.id}>{g.year} · {g.title}</option>)}
              </select>
            </div>
          )}
        </div>

        {SHOWS_PROGRESS.has(form.scope) && (
          <div className="form-group">
            <label>Fortschritt: {form.progress || 0}%</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input type="range" min={0} max={100} step={5} value={form.progress || 0}
                onChange={e => set('progress', parseInt(e.target.value, 10))}
                style={{ flex: 1, width: 'auto' }} />
              <input type="number" min={0} max={100} step={5} value={form.progress || 0}
                onChange={e => set('progress', Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)))}
                style={{ width: 80 }} />
            </div>
          </div>
        )}

        {/* Parent */}
        <div className="form-group">
          <label>Übergeordneter Task (Parent)</label>
          <ParentPicker
            value={form.parent_id}
            options={parentOptions}
            onChange={(id) => set('parent_id', id)}
          />
        </div>

        {/* Recurring */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <input type="checkbox" id="recurring" checked={form.is_recurring}
            onChange={e => set('is_recurring', e.target.checked)} style={{ width: 'auto' }} />
          <label htmlFor="recurring" style={{ margin: 0, cursor: 'pointer' }}>Wiederkehrender Task</label>
          {form.is_recurring && (
            <select value={form.recurring_interval || ''} onChange={e => set('recurring_interval', e.target.value)} style={{ width: 'auto', flex: 1 }}>
              <option value="">-- Intervall wählen --</option>
              <option value="daily">Täglich</option>
              <option value="weekly">Wöchentlich</option>
              <option value="monthly">Monatlich</option>
            </select>
          )}
        </div>

        {/* Categories */}
        {categories.length > 0 && (
          <div className="form-group">
            <label>Kategorien</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {categories.map(cat => (
                <button type="button" key={cat} className={`badge`} onClick={() => toggleCat(cat)}
                  style={{
                    cursor: 'pointer', border: '1px solid',
                    borderColor: form.categories.includes(cat) ? 'var(--accent)' : 'var(--border)',
                    background: form.categories.includes(cat) ? 'var(--accent-dim)' : 'transparent',
                    color: form.categories.includes(cat) ? 'var(--accent)' : 'var(--text-2)',
                    padding: '4px 10px',
                  }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Links */}
        <div className="form-group">
          <label>Externe Links</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {form.links.map((link, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Link size={13} color="var(--text-3)" />
                <span style={{ color: 'var(--accent)', fontSize: 12, flex: 1 }}>{link.label || link.url}</span>
                <button type="button" className="btn btn-icon btn-ghost btn-sm" onClick={() => set('links', form.links.filter((_, j) => j !== i))}><X size={12} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newLink.url} onChange={e => setNewLink(l => ({ ...l, url: e.target.value }))} placeholder="https://..." style={{ flex: 2 }} />
              <input value={newLink.label} onChange={e => setNewLink(l => ({ ...l, label: e.target.value }))} placeholder="Bezeichnung" style={{ flex: 1 }} />
              <button type="button" className="btn btn-ghost btn-sm" onClick={addLink}><Plus size={13} /></button>
            </div>
          </div>
        </div>

        <hr className="divider" />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {task && (
            <>
              <button type="button" className="btn btn-ghost"
                onClick={() => { startPomodoro(task); onClose(); }}
                title="25 Minuten Fokuszeit für diesen Task starten">
                <Timer size={14} /> Pomodoro starten
              </button>
              <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }}
                onClick={() => setConfirmDelete(true)}
                title="Diesen Task endgültig löschen">
                <Trash2 size={14} /> Löschen
              </button>
            </>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>{task ? 'Schließen' : 'Abbrechen'}</button>
          {!task && (
            <button type="button" className="btn btn-primary" onClick={create} disabled={creating || !form.title.trim()}>
              {creating ? 'Erstellen...' : 'Erstellen'}
            </button>
          )}
        </div>
      </div>
      {confirmDelete && (
        <Confirm
          message={`Task „${task?.title || ''}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </Modal>
  );
}
